"use client";

import { useEffect, useRef } from 'react';

interface ShaderBackgroundProps {
  className?: string;
  color?: [number, number, number];
  speed?: number;
  amplitude?: number;
}

// Simplified CSS-based animated background as fallback
function CSSAnimatedBackground({ className, color = [0.8, 0.4, 1] }: ShaderBackgroundProps) {
  const rgbColor = color.map(c => Math.round(c * 255)).join(', ');
  
  return (
    <div 
      className={`absolute inset-0 ${className || ''}`}
      style={{
        background: `
          radial-gradient(circle at 20% 50%, rgba(${rgbColor}, 0.3) 0%, transparent 50%),
          radial-gradient(circle at 80% 20%, rgba(${rgbColor}, 0.2) 0%, transparent 50%),
          radial-gradient(circle at 40% 80%, rgba(${rgbColor}, 0.25) 0%, transparent 50%),
          linear-gradient(45deg, #000000, #111111)
        `,
        animation: 'gradientShift 8s ease infinite',
      }}
    >
      {/* Additional animated elements for visual interest */}
      <div 
        className="absolute inset-0 opacity-30"
        style={{
          background: `
            radial-gradient(ellipse at center, rgba(${rgbColor}, 0.1) 0%, transparent 70%)
          `,
          animation: 'float 6s ease-in-out infinite',
        }}
      />
    </div>
  );
}

const vertexShader = `
attribute vec2 a_position;
attribute vec2 a_texCoord;
varying vec2 v_texCoord;

void main() {
  gl_Position = vec4(a_position, 0, 1);
  v_texCoord = a_texCoord;
}
`;

const fragmentShader = `
precision highp float;
uniform float u_time;
uniform vec3 u_color;
uniform vec2 u_resolution;
uniform vec2 u_mouse;
uniform float u_amplitude;
uniform float u_speed;
varying vec2 v_texCoord;

void main() {
  float mr = min(u_resolution.x, u_resolution.y);
  vec2 uv = (v_texCoord.xy * 2.0 - 1.0) * u_resolution.xy / mr;
  uv += (u_mouse - vec2(0.5)) * u_amplitude;
  
  float d = -u_time * 0.5 * u_speed;
  float a = 0.0;
  
  for (float i = 0.0; i < 8.0; ++i) {
    a += cos(i - d - a * uv.x);
    d += sin(uv.y * i + a);
  }
  
  d += u_time * 0.5 * u_speed;
  vec3 col = vec3(cos(uv * vec2(d, a)) * 0.6 + 0.4, cos(a + d) * 0.5 + 0.5);
  col = cos(col * cos(vec3(d, a, 2.5)) * 0.5 + 0.5) * u_color;
  
  gl_FragColor = vec4(col, 1.0);
}
`;

function createShader(gl: WebGLRenderingContext, type: number, source: string): WebGLShader | null {
  const shader = gl.createShader(type);
  if (!shader) return null;
  
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    console.warn('Shader compile error:', gl.getShaderInfoLog(shader));
    gl.deleteShader(shader);
    return null;
  }
  
  return shader;
}

function createProgram(gl: WebGLRenderingContext, vertexShader: WebGLShader, fragmentShader: WebGLShader): WebGLProgram | null {
  const program = gl.createProgram();
  if (!program) return null;
  
  gl.attachShader(program, vertexShader);
  gl.attachShader(program, fragmentShader);
  gl.linkProgram(program);
  
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    console.warn('Program link error:', gl.getProgramInfoLog(program));
    gl.deleteProgram(program);
    return null;
  }
  
  return program;
}

export function ShaderBackground({ 
  className, 
  color = [0.8, 0.4, 1], 
  speed = 0.6, 
  amplitude = 0.12 
}: ShaderBackgroundProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const mouseRef = useRef({ x: 0.5, y: 0.5 });
  const fallbackRef = useRef(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Try WebGL first, fallback to CSS animation
    const gl = (canvas.getContext('webgl') || canvas.getContext('experimental-webgl')) as WebGLRenderingContext | null;
    if (!gl) {
      fallbackRef.current = true;
      return;
    }

    // Create shaders
    const vShader = createShader(gl, gl.VERTEX_SHADER, vertexShader);
    const fShader = createShader(gl, gl.FRAGMENT_SHADER, fragmentShader);
    
    if (!vShader || !fShader) {
      fallbackRef.current = true;
      return;
    }

    // Create program
    const program = createProgram(gl, vShader, fShader);
    if (!program) {
      fallbackRef.current = true;
      return;
    }

    // Get attribute and uniform locations
    const positionLocation = gl.getAttribLocation(program, 'a_position');
    const texCoordLocation = gl.getAttribLocation(program, 'a_texCoord');
    const timeLocation = gl.getUniformLocation(program, 'u_time');
    const colorLocation = gl.getUniformLocation(program, 'u_color');
    const resolutionLocation = gl.getUniformLocation(program, 'u_resolution');
    const mouseLocation = gl.getUniformLocation(program, 'u_mouse');
    const amplitudeLocation = gl.getUniformLocation(program, 'u_amplitude');
    const speedLocation = gl.getUniformLocation(program, 'u_speed');

    // Create buffers
    const positionBuffer = gl.createBuffer();
    const texCoordBuffer = gl.createBuffer();

    // Full screen quad positions
    const positions = new Float32Array([
      -1, -1,
       1, -1,
      -1,  1,
      -1,  1,
       1, -1,
       1,  1,
    ]);

    // Texture coordinates
    const texCoords = new Float32Array([
      0, 0,
      1, 0,
      0, 1,
      0, 1,
      1, 0,
      1, 1,
    ]);

    // Bind position buffer
    gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, positions, gl.STATIC_DRAW);

    // Bind texture coord buffer
    gl.bindBuffer(gl.ARRAY_BUFFER, texCoordBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, texCoords, gl.STATIC_DRAW);

    let animationId: number;
    let startTime = Date.now();

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width;
      canvas.height = rect.height;
      gl.viewport(0, 0, canvas.width, canvas.height);
    };

    const handleMouseMove = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      mouseRef.current.x = (e.clientX - rect.left) / rect.width;
      mouseRef.current.y = 1.0 - (e.clientY - rect.top) / rect.height;
    };

    const animate = () => {
      const currentTime = Date.now();
      const time = (currentTime - startTime) * 0.001;

      // Clear canvas
      gl.clearColor(0, 0, 0, 1);
      gl.clear(gl.COLOR_BUFFER_BIT);

      // Use program
      gl.useProgram(program);

      // Set up position attribute
      gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
      gl.enableVertexAttribArray(positionLocation);
      gl.vertexAttribPointer(positionLocation, 2, gl.FLOAT, false, 0, 0);

      // Set up texture coordinate attribute
      gl.bindBuffer(gl.ARRAY_BUFFER, texCoordBuffer);
      gl.enableVertexAttribArray(texCoordLocation);
      gl.vertexAttribPointer(texCoordLocation, 2, gl.FLOAT, false, 0, 0);

      // Set uniforms
      gl.uniform1f(timeLocation, time);
      gl.uniform3f(colorLocation, color[0], color[1], color[2]);
      gl.uniform2f(resolutionLocation, canvas.width, canvas.height);
      gl.uniform2f(mouseLocation, mouseRef.current.x, mouseRef.current.y);
      gl.uniform1f(amplitudeLocation, amplitude);
      gl.uniform1f(speedLocation, speed);

      // Draw
      gl.drawArrays(gl.TRIANGLES, 0, 6);

      animationId = requestAnimationFrame(animate);
    };

    // Initialize
    resize();
    animate();

    // Event listeners
    window.addEventListener('resize', resize);
    canvas.addEventListener('mousemove', handleMouseMove);

    return () => {
      window.removeEventListener('resize', resize);
      canvas.removeEventListener('mousemove', handleMouseMove);
      cancelAnimationFrame(animationId);
      
      // Cleanup WebGL resources
      gl.deleteProgram(program);
      gl.deleteShader(vShader);
      gl.deleteShader(fShader);
      gl.deleteBuffer(positionBuffer);
      gl.deleteBuffer(texCoordBuffer);
    };
  }, [color, speed, amplitude]);

  // If WebGL is not supported or failed, use CSS fallback
  if (fallbackRef.current) {
    return <CSSAnimatedBackground className={className} color={color} speed={speed} amplitude={amplitude} />;
  }

  return (
    <div className={`absolute inset-0 shader-background ${className || ''}`}>
      <canvas
        ref={canvasRef}
        className="w-full h-full block"
      />
    </div>
  );
}